/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import { TranslatedString } from "@gnu-taler/taler-util";
import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { ComponentChildren, Ref, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { PASSWORD_MAX_LENGTH } from "./auth-validation.js";

export function AuthCard({
  title,
  subtitle,
  children,
  footer,
}: {
  title: TranslatedString;
  subtitle?: TranslatedString;
  children: ComponentChildren;
  footer?: ComponentChildren;
}): VNode {
  return (
    <div class="mx-auto w-full max-w-md py-2 sm:py-6">
      <div class="text-center">
        <h1 class="text-2xl font-bold leading-9 tracking-tight text-onBackground">
          {title}
        </h1>
        {subtitle ? (
          <p class="mt-2 text-sm leading-6 text-gray-600">{subtitle}</p>
        ) : undefined}
      </div>
      <div class="mt-6 rounded-lg border border-gray-200 bg-white px-5 py-6 shadow-sm sm:px-8">
        {children}
      </div>
      {footer ? (
        <div class="mt-5 text-center text-sm text-gray-600">{footer}</div>
      ) : undefined}
    </div>
  );
}

export function AuthAlert({
  type,
  children,
}: {
  type: "error" | "success" | "warning";
  children: ComponentChildren;
}): VNode {
  const classes =
    type === "error"
      ? "border-red-200 bg-red-50 text-red-800"
      : type === "success"
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div
      class={`mb-5 rounded-md border px-3 py-2 text-sm ${classes}`}
      role={type === "error" ? "alert" : "status"}
    >
      {children}
    </div>
  );
}

export function FieldError({
  id,
  message,
  visible,
}: {
  id: string;
  message?: TranslatedString;
  visible: boolean;
}): VNode {
  return (
    <p id={id} class="mt-1 min-h-5 text-sm text-red-700">
      {visible ? message : undefined}
    </p>
  );
}

export function PasswordField({
  id,
  name,
  label,
  value,
  error,
  showError,
  autoComplete,
  inputRef,
  enterKeyHint,
  onInput,
  onBlur,
}: {
  id: string;
  name: string;
  label: TranslatedString;
  value: string;
  error?: TranslatedString;
  showError: boolean;
  autoComplete: "current-password" | "new-password";
  inputRef?: Ref<HTMLInputElement>;
  enterKeyHint?: "next" | "go" | "done";
  onInput(value: string): void;
  onBlur(): void;
}): VNode {
  const { i18n } = useTranslationContext();
  const [visible, setVisible] = useState(false);
  const errorId = `${id}-error`;
  const invalid = showError && !!error;

  return (
    <div>
      <label
        htmlFor={id}
        class="block text-sm font-medium leading-6 text-onBackground"
      >
        {label}
      </label>
      <div class="relative mt-2">
        <input
          ref={inputRef}
          type={visible ? "text" : "password"}
          name={name}
          id={id}
          autocomplete={autoComplete}
          maxLength={PASSWORD_MAX_LENGTH}
          class="block w-full rounded-md border-0 py-1.5 pr-16 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
          enterkeyhint={enterKeyHint}
          value={value}
          required
          aria-invalid={invalid}
          aria-describedby={invalid ? errorId : undefined}
          onInput={(event) => onInput(event.currentTarget.value)}
          onBlur={onBlur}
        />
        <button
          type="button"
          class="absolute inset-y-0 right-0 px-3 text-xs font-semibold text-primaryDark hover:text-onPrimaryContainer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-pressed={visible}
          aria-label={
            visible ? i18n.str`Hide password` : i18n.str`Show password`
          }
          onClick={() => setVisible((current) => !current)}
        >
          {visible ? (
            <i18n.Translate>Hide</i18n.Translate>
          ) : (
            <i18n.Translate>Show</i18n.Translate>
          )}
        </button>
      </div>
      <FieldError id={errorId} message={error} visible={showError} />
    </div>
  );
}
